import React from 'react';

import { useAppSelector } from '@fd/customHooks/useAppSelector';
import MenuIcon from '@mui/icons-material/Menu';
import AppBar from '@mui/material/AppBar';
import IconButton from '@mui/material/IconButton';
import { type Theme, useTheme } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import useMediaQuery from '@mui/material/useMediaQuery';
import makeStyles from '@mui/styles/makeStyles';

import { ReactComponent as FlipdishLogo } from '../../assets/images/FlipdishLogo.svg';
import { useHasAccessToOnboardingV3 } from '../../components/OnboardingV3/hooks/useHasAccessToOnboardingV3';
import { ProfileMenu } from '../../components/ProfileMenu';
import BrandSelector from '../../components/RMS/BrandSelector/BrandSelector';
import { SupportMenu } from '../../components/SupportMenu';
import { flagService } from '../../services/flagService/flagService';
import ErrorBoundary from './ErrorBoundary';
import HelpMenu from './HelpMenu';
import PortalSearch from './Search/PortalSearch';
import TopNavMobileExpandedMenu from './TopNavMobileExpandedMenu';

const useStyles = makeStyles((theme: Theme) => ({
  appBar: {
    zIndex: 'auto',
    backgroundColor: '#fff',
    borderBottom: 'solid 1px #dbdcdf',
  },
  menuButton: {
    marginRight: theme.spacing(1.5),

    [theme.breakpoints.down('sm')]: {
      marginRight: 0,
    },
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
  },
  switch: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  holder: {
    [theme.breakpoints.down('sm')]: {
      marginLeft: 'auto',
    },
  },
  subBar: {
    display: 'flex',
    marginLeft: 'auto',
    alignItems: 'center',
  },
  buttonContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    textTransform: 'none',
  },
  icon: {
    width: '16px',
    height: '18px',
  },
  button: {
    marginLeft: theme.spacing(1.5),
  },
  searchBar: {
    width: '100%',
    maxWidth: '800px',
    [theme.breakpoints.up('md')]: {
      margin: `0px ${theme.spacing(6)}`,
    },
    [theme.breakpoints.down('md')]: {
      margin: `0px 0px 0px ${theme.spacing(2)}`,
    },
  },
}));

type Props = {
  open: boolean;
  toggleSideNav: () => void;
};

const TopNav = ({ toggleSideNav }: Props) => {
  const { onboardingStatus, isFlipdishStaff } = useHasAccessToOnboardingV3();
  const classes = useStyles();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const isShowPortalSearchOn = useAppSelector((state) =>
    flagService.isFlagOn(state, 'showPortalSearch')
  );
  const reducedOnboardingView = onboardingStatus === 'in_progress' && !isFlipdishStaff;
  return (
    <AppBar
      enableColorOnDark
      color="default"
      elevation={0}
      position="static"
      className={classes.appBar}
    >
      <Toolbar>
        <IconButton
          onClick={toggleSideNav}
          color="inherit"
          aria-label="Open drawer"
          data-fd="drawer_toggle"
          edge="start"
          className={classes.menuButton}
        >
          <MenuIcon />
        </IconButton>

        <div>
          <FlipdishLogo />
        </div>

        {!reducedOnboardingView && isShowPortalSearchOn && (
          <div className={classes.searchBar}>
            <ErrorBoundary identifier="PortalSearch" includeTowlie={false}>
              <PortalSearch />
            </ErrorBoundary>
          </div>
        )}

        <div className={classes.subBar}>
          {!reducedOnboardingView && (
            <>
              {isDesktop ? (
                <>
                  <SupportMenu />
                  <HelpMenu />
                </>
              ) : (
                <TopNavMobileExpandedMenu />
              )}
            </>
          )}
          {isDesktop && <ProfileMenu />}
          <div className={classes.holder}>
            <BrandSelector />
          </div>
        </div>
      </Toolbar>
    </AppBar>
  );
};

export default TopNav;
